import { onBeforeUnmount, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

import { useNotificationsStore } from '@/features/notifications/stores/useNotificationsStore';
import { showToast } from '@/plugins/toast';

/**
 * useNotificationServiceWorker — bridges the push service worker to the in-app
 * notifications store.
 *
 * When a web push arrives while the portal tab is visible, the service worker
 * (`src/service-worker.js` → `pushNotificationDispatch`) suppresses the OS-level
 * toast and `postMessage`s a `profiteia:notification` payload to the page. This
 * composable listens for that message, shows an in-app toast, and refreshes the
 * feed + unread counter so the bell badge and any open notification panel update
 * immediately — without waiting for the 30 s polling tick.
 *
 * Mount it once, from `App.vue`. It self-manages its `navigator.serviceWorker`
 * `message` listener across the component lifecycle.
 */
export function useNotificationServiceWorker() {
  const { t } = useI18n();
  const notificationsStore = useNotificationsStore();

  const handleServiceWorkerMessage = (event) => {
    const data = event && event.data;
    if (!data || data.type !== 'profiteia:notification') return;
    const notificationText = [data.title, data.body].filter(Boolean).join(' - ');
    showToast({
      text: notificationText || t('messages.notification_received'),
      tone: 'info',
      durationMs: 5000,
    });
    notificationsStore.refresh().catch(() => {
      // Best-effort: the next poll tick will reconcile.
    });
  };

  onMounted(() => {
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }
  });

  onBeforeUnmount(() => {
    if (navigator.serviceWorker) {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    }
  });
}
